import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import listingService from '../services/listingService';

const NOTICE = 'Payment received — an Eqaya verifier will be offered your property shortly.';
// Without a settled session we do not actually know the money landed; say what
// is true rather than promising a verifier that may never be dispatched.
const HEDGED = 'We\'re confirming your payment — this page will update shortly.';

/**
 * Where Stripe sends the realtor after the listing verification fee is paid.
 * The confirm call is a belt-and-braces settle: the webhook usually gets there
 * first, so a failure here is not worth showing — the listing page tells the
 * realtor where the check actually stands.
 */
const ListingVerificationPaymentSuccess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [working, setWorking] = useState(true);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        if (!sessionId) throw new Error('no session_id on the return url');
        await listingService.confirmVerificationPayment(sessionId);
        if (!dead) setConfirmed(true);
      } catch (e) {
        // The webhook is the authority and usually settles it anyway, so this
        // is a hedge, not a failure — but it should be visible in the console.
        console.warn('listing verification payment not confirmed on return', e?.response?.data?.error || e?.message);
      } finally { if (!dead) setWorking(false); }
    })();
    return () => { dead = true; };
  }, [sessionId]);

  const back = useCallback((replace) => navigate(`/realtor/listings/${id}`, { replace, state: { notice: confirmed ? NOTICE : HEDGED } }), [confirmed, id, navigate]);

  useEffect(() => {
    if (working) return undefined;
    const t = setTimeout(() => back(true), 1200);
    return () => clearTimeout(t);
  }, [working, back]);

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, md: 9 } }}>
      <Paper elevation={1} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, textAlign: 'center' }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 58, mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Payment received</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your Eqaya Verification is booked. We are matching your property with a verifier now.
        </Typography>
        {working && <Box sx={{ py: 2 }}><CircularProgress size={28} /></Box>}
        <Button variant="contained" onClick={() => back(true)}>Back to the listing</Button>
      </Paper>
    </Container>
  );
};

export default ListingVerificationPaymentSuccess;
